import { useGeneratedPricingVariantPackDetailedCostChangeDataQuery } from '@sales-portal/domain/pricing/screens/management/blocs/documents/variant-pack-detailed-cost-change-data.generated';
import { useMemo } from 'react';

/**
 * Hook to load detailed cost change data for a specific variant pack.
 *
 * @param supplierUUID - The unique identifier of the supplier.
 * @param variantPackUUID - The unique identifier of the variant pack.
 * @returns An object containing the detailed cost change data, loading state, error, and refetch function.
 *
 * @example
 * ```typescript
 * const { data, loading } = useVariantPackDetailedCostChangeDataLoader(
 *   supplierUUID,
 *   variantPackUUID
 * );
 * ```
 */
export const useVariantPackDetailedCostChangeDataLoader = (
  supplierUUID: string | undefined,
  variantPackUUID: string,
) => {
  const { data, loading, error, refetch } =
    useGeneratedPricingVariantPackDetailedCostChangeDataQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: { supplierUUID, variantPackUUID },
    });

  return useMemo(
    () => ({
      data,
      error,
      loading,
      refetch,
    }),
    [data, error, loading, refetch],
  );
};
